import React, { useState, useContext, KeyboardEvent, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@state/actions';
import selectors from '@state/selectors';
import {IProps, IState} from './types';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { ITemplate } from '@state/templates/types';
import { IDesign, PermissionEnum } from '@state/designs/types';
import * as S from './styles';
import C from '@assets/themes/colors';
import Button from '@components/Button';
import { Context } from '@routes/private/Editor/Context';
import { ArrowLeftIcon } from '@assets/icons/arrowLeftIcon';
import { useHistory } from 'react-router';
import MenuOptions from './MenuOptions';
import Tooltip from '@components/Tooltip';
import ErrorIcon from '@assets/icons/errorIcon';
import { cloneDeep } from 'lodash';
import EditorOptions from '../AppBar/EditorOptions';
import IconButton from '@components/IconButton';
import OptionIcon from '@assets/icons/optionIcon';
import { IUserAccess } from '@state/userAccess/types';
import { handleRemoveBrand, isAuthenticated } from '@utils/userAccess';
import { fetchIndustries } from '@state/industries/utils';


const CommandBars:React.FC<IProps> = (props) => {
  const authContext = useContext(AuthContext);
  const userAccess: IUserAccess | undefined = authContext.userAccess;
  const industriesOptions = useSelector(selectors.industries.getIndustries);
  const { editorStatus, entityInformation, onWarningScreenChange, onEditEntityInformation, onUpdateEntityInformation, parsedTreeInfo, userPermission } = useContext(Context);
  const templateInformation = entityInformation?.entityType === "TEMPLATE" ? (entityInformation as ITemplate) : undefined;
  const designInformation = entityInformation?.entityType === "DESIGN" ? (entityInformation as IDesign) : undefined;
  const profile = authContext.userData?.profile;
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [title, setTitle] = useState(entityInformation?.name??"");
  const [showError, setShowError] = useState(false);
  const [openOptions, setOpenOptions] = useState(false);
  const [coordinates, setCoordinates] = useState({x: 0, y: 0});
  const inputRef = useRef(null);
  const history: any = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [title])

  const handleClickOutside = (e: any)=> {
    if(inputRef && inputRef.current){
        const ref: any = inputRef.current
        if(!ref.contains(e.target))handleUpdateTitle(undefined, true);
    }
  }

  const closeWarningScreen = () => {
    onWarningScreenChange(false, "", ``, "", undefined, "", "", []);
  }

  const errorWarningScreen = () => {
    onWarningScreenChange(
      true, "Publish Template Error",
      `The publish template status could not be updated. \nTry it later...`,
      "", undefined, "OKAY", "", []
    );
  }

  const handlePublish = async (action: string) => {
    let newTemplate = templateInformation;
    if (newTemplate && saving === false) {
      setSaving(true);
      newTemplate.templateStatus = action;
      const userData = {
        email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
      };
      const newPublishedDate = {
        user: userData,
        date: new Date(),
        action: action
      };
      if (newTemplate.publishedInfo) newTemplate.publishedInfo = [...newTemplate.publishedInfo, newPublishedDate]
      else newTemplate.publishedInfo = [newPublishedDate];
      if (action === "PUBLISHED") closeWarningScreen();
      const result: any = await onEditEntityInformation(newTemplate);
      if (result?.status !== 200) errorWarningScreen();
      setSaving(false);
    };
  }

  const handlePublishTemplate = () => {
    if (templateInformation?.templateStatus === "UNPUBLISHED") {
      onWarningScreenChange(
        true, "Publish Template",
        `Once you publish this template, it will become available for use by others on your team. 
        \nYou can unpublish or deactivate a template at any time.`,
        "CANCEL", () => handlePublish("PUBLISHED"), "PUBLISH", "", []
      );
    } else {
      handlePublish("UNPUBLISHED");
    }
  }

  const handleBack = () => {
    handleRemoveBrand(dispatch, userAccess, industriesOptions, entityInformation.tags.oem)
    authContext.setLoggedToken(false);
    if (templateInformation) history.push({pathname: '/templates'});
    else history.push({pathname: '/studio'});
  }

  const fetchNames = () => {
    const query = `?filter=c.id<>"${entityInformation.id}" and c.formatName="${encodeURIComponent(title.toLocaleLowerCase())}" and c.${entityInformation?.entityType==="DESIGN"?"designStatus":"templateStatus"}<>"INACTIVE"&orderby=c.createdDate DESC&start=0&pagelength=20`;    
    return new Promise((resolve, reject) => {
      if (entityInformation?.entityType==="DESIGN"){
        dispatch(actions.designs.fetchDesigns(query, (result, error) => {
          if (error) reject(error)
          else resolve(result)
        }))
      } else {
        dispatch(actions.templates.fetchTemplates(query, (result, error) => {
          if (error) reject(error)
          else resolve(result)
        }))
      }
    });
  }

  const updateEntityName = () => {
    return new Promise((resolve, reject) => {
      const newEntityInformation: IDesign|ITemplate = {...cloneDeep(entityInformation), name: title};
      onUpdateEntityInformation(newEntityInformation);
      if (entityInformation?.entityType==="DESIGN"){
        dispatch(actions.designs.updateDesign((newEntityInformation as IDesign),"", (result: any, error: any) => {
          if (error) reject(error)
          else resolve(result);
      }))
      } else {
        dispatch(actions.templates.updateTemplate((newEntityInformation as ITemplate), (result: any, error: any) => {
          if (error) reject(error)
          else resolve(result);
      }))
      }
    });
  }

  const handleUpdateTitle = async(e?: KeyboardEvent<HTMLInputElement>, clickOutside?: boolean) => {
    if (e?.key === "Enter" || clickOutside) {
      if (title==="") setTitle(entityInformation?.name);
      else {      
        const result = await fetchNames();   
        if (result && (result as any[]).length>0) {
          setShowError(true);
          setTitle(entityInformation?.name);
        } else {
          await updateEntityName();
          setEditTitle(false);
        }
      }
    }
  }

  const hadleChange = (e: any) => {
    setTitle(e.target.value); 
    setShowError(false);
  }

  const handleOpenOptions = (e: any) => {
    e.stopPropagation();
    setOpenOptions(!openOptions);
    setCoordinates({x: e.clientX, y: e.clientY});
  }

  const duplicateTemplate = (id: string) => {
    return new Promise((resolve, reject) => {
      const userData = {
        email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
      };
      dispatch(actions.templates.duplicateTemplate({id: id,userData: userData}, (result: any, error: any) => {
        if (error) {
          reject(error);
        }
        else {
          resolve(result);
        }
      }))
    })
  }

  const duplicateDesign = (id: string, owner: string) => {
    return new Promise((resolve, reject) => {
      const userData = {
        email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
      };
      let keepFolder = owner===userData.email;

      dispatch(actions.designs.duplicateDesign({id: id,userData: userData, keepFolder: keepFolder, updateFolderCounter: keepFolder}, (result: any, error: any) => {
        if (error) {
          reject(error);
        }
        else {
          resolve(result);
        }
      }))
    })
  }

  const handleDuplicateTemplate = async () => {
    try {
      const templateDuplicated: any = await duplicateTemplate(templateInformation!.id);
      window.open(`/editor/template/${templateDuplicated.id}`);
    } catch (error) {
      onWarningScreenChange(
        true, "Create a Copy",
        `The template was not duplicated, please try later.`,
        "", undefined, "OKAY", "Duplicate template process failure",[]
      );
    }
  }

  const handleDuplicateDesign = async () => {
    try {
      const designDuplicated: any = await duplicateDesign(designInformation!.id, designInformation!.createdBy.email);
      window.open(`/editor/design/${designDuplicated.id}`);
    } catch (error) {
      onWarningScreenChange(
        true, "Create a Copy",
        `The design was not duplicated, please try later.`,
        "", undefined, "OKAY", "Duplicate design process failure",[]
      );
    }
  }


  const handleCreateACopy = () => {
    if (templateInformation) {
      handleDuplicateTemplate();
    }
    else {
      handleDuplicateDesign();
    }
  }

const handleActions = (action: string) => {
    if (action==="EDITNAME") {
      setEditTitle(true); 
      setShowError(false);
    }
    else if (action==="CREATEACOPY") {
      handleCreateACopy();
    } 
  }

  const handleTitleDoubleClick = () => {
    if (userPermission === PermissionEnum.EDIT) {
      setEditTitle(true); 
      setShowError(false);
    }
  }

  const bringIndustries = async () => {
    if (!industriesOptions.length) {
      await fetchIndustries(dispatch)
    }
  }

  useEffect(() => {
    bringIndustries()
  }, [])

  const handleViewBaseOnPermissions = () => {
    const isUserAuthenticated = isAuthenticated(authContext.userData);
  
    return (
      <S.Root>
        <S.BarContainer>
          {profile?.role !== "Public" && (
            <S.BackContainer onClick={() => handleBack()}>
              <ArrowLeftIcon style={{ marginRight: "5.5px" }} />
              {templateInformation ? "BACK TO TEMPLATE LIST" : "BACK TO STUDIO DASHBOARD"}
            </S.BackContainer>
          )}
  
          {!editTitle ? (
            <>
              <Tooltip
                tip={userPermission === PermissionEnum.EDIT ? "Double click to edit file name, and hit enter to save" : undefined}
                boxStyle={{ left: "unset", right: "-299px", top: "0px" }}
                style={{ borderRadius: "2px" }}
              >
                <S.TitleContainer onDoubleClick={handleTitleDoubleClick}>{title}</S.TitleContainer>
              </Tooltip>
  
              {(profile?.role !== "Public" && userPermission !== PermissionEnum.NONE && userPermission !== PermissionEnum.PUBLIC_VIEW) && (
                <S.MetaDataOptionIconContainer>
                  <IconButton
                    containerStyles={{ transform: "scale(0.7)" }}
                    action={handleOpenOptions}
                    hoverBackgroundColor={"rgba(255, 255, 255, 0.2)"}
                  >
                    <OptionIcon style={{ fillColor: C.WHITE }} />
                  </IconButton>
                </S.MetaDataOptionIconContainer>
              )}
            </>
          ) : (
            <S.InputContainer>
              <S.TitleInput
                ref={inputRef}
                autoComplete="off"
                autoFocus={true}
                id={"title-edit"}
                value={title}
                onChange={(e: any) => hadleChange(e)}
                onKeyPress={(e: any) => handleUpdateTitle(e)}
              />
              <S.ErrorMessage showError={showError}>
                <S.TextTitle>
                  <ErrorIcon style={{ marginRight: "7px" }} />
                  ERROR: Changes not saved because name already exists. No two files can share the same name.
                </S.TextTitle>
              </S.ErrorMessage>
            </S.InputContainer>
          )}
  
          {editorStatus.showSelectDescribe && templateInformation ? (
            <Button
              label={
                saving === true
                  ? "SAVING..."
                  : templateInformation?.templateStatus === "UNPUBLISHED"
                  ? "PUBLISH TEMPLATE"
                  : "UNPUBLISH TEMPLATE"
              }
              style={{
                hoverBackgroundColor: "#344a70",
                backgroundColor: "#19325d",
                padding: "10px 11px",
                float: "right",
                width: "155px",
                marginRight: 53,
                marginLeft: "auto",
              }}
              onClick={handlePublishTemplate}
              disabled={false}
            />
          ) : (
            <MenuOptions designName={title} />
          )}
        </S.BarContainer>
  
        {openOptions && (
          <EditorOptions
            open={openOptions}
            setOpen={setOpenOptions}
            coordinates={coordinates}
            labels={{
              name: title,
              editedBy: parsedTreeInfo?.updatedBy?.name || "",
              lastEditedDate: parsedTreeInfo?.updateDate
                ? new Intl.DateTimeFormat("en-US", { day: "2-digit", month: "2-digit", year: "2-digit" }).format(
                    new Date(parsedTreeInfo.updateDate)
                  )
                : "",
            }}
            onActions={handleActions}
          />
        )}
      </S.Root>
    );
  };
  

  return handleViewBaseOnPermissions();
}

export default CommandBars;